/* eslint-disable jsx-a11y/control-has-associated-label */
import React from 'react';

import OfferPrice from '@/components/av-check/OfferPrice';
import {
  CmsPageContentIdentifier,
  CmsPageContentType,
  CmsTariffContentType,
  FixedLineAccessTechnologyType,
  TariffOfferType,
} from '@/types/av-check';
import { buildContinueWithSelectedTariffDataPopup, pushToTefDataLayer } from '@/services/tracking';
import { formatDate, formatDateWithoutDate, interpolate, isExpansionPeriodEndInFuture } from '@/utils';

export interface Props {
  tariffOffer: TariffOfferType,
  offerHint?: string,
  buttonText: string,
  dispatchEvent?: Function,
  originalOfferName?: string,
  technologyPricingLabels: { DISCOUNT: string, SURCHARGE: string, MONTHLYLIST: string },
  cms: CmsPageContentType,
  setShowDTAGSpecificationView: (showDTAGSpecificationView: boolean) => void,
  dtagSpecificAvChecked: boolean,
  setDtagSpecificAvChecked: (dtagSpecificAvCheck: boolean) => void,
  setSelectedOfferName: (selectedOfferName: string) => void,
  setShowPdpActivationCostInfo?: (showPdpActivationCostInfo: boolean) => void,
  setShowPdpDevelopmentCostInfo?: (showPdpDevelopmentCostInfo: boolean) => void,
  isDtagToggleShopEnabled: boolean,
  isDtagToggleCciEnabled: boolean,
  isDtagGuiEnhancementEnabled: boolean,
  isDtagGuiEnhancementCCIEnabled: boolean,
  originalOffer?: TariffOfferType,
  originalTariffOfferName: string,
}

const Item = ({
  tariffOffer,
  offerHint,
  buttonText,
  dispatchEvent,
  technologyPricingLabels,
  cms,
  setShowDTAGSpecificationView,
  dtagSpecificAvChecked,
  setDtagSpecificAvChecked,
  setSelectedOfferName,
  setShowPdpActivationCostInfo,
  setShowPdpDevelopmentCostInfo,
  isDtagToggleShopEnabled,
  isDtagToggleCciEnabled,
  isDtagGuiEnhancementEnabled,
  isDtagGuiEnhancementCCIEnabled,
  originalOffer,
  originalTariffOfferName,
}: Props) => {

  const {
    cmsTariffDescription,
    charges,
    url,
  } = tariffOffer;

  const offerChanged = originalTariffOfferName != tariffOffer.offerName;

  const isDtagToggleEnabled: boolean | undefined = isDtagToggleShopEnabled || isDtagToggleCciEnabled;
  const isDtagEnhancementToggleEnabled: boolean | undefined = isDtagGuiEnhancementEnabled || isDtagGuiEnhancementCCIEnabled;

  const isDtagFtthTariff = tariffOffer.accessType == 'DTAG-FTTH-ETH';
  const isUggFtthTariff = tariffOffer.accessType == 'UGG-FTTH-ETH';

  const isBeforePrepared: boolean | undefined = isDtagFtthTariff ?
    !tariffOffer.technologyAvailability.homeIdPossible : undefined;

  const expansionStatusEndDate: string | undefined = isDtagFtthTariff && tariffOffer.technologyAvailability.expansionPeriodEnd
    ? formatDateWithoutDate(tariffOffer.technologyAvailability.expansionPeriodEnd)
    : undefined;

  const earliestActivationDate: string | undefined = isUggFtthTariff && tariffOffer.earliestActivationDate
    ? formatDate(tariffOffer.earliestActivationDate)
    : undefined;

  const isExpansionEndDateInFuture: boolean | undefined = isDtagFtthTariff && tariffOffer.technologyAvailability.expansionPeriodEnd ?
    isExpansionPeriodEndInFuture(tariffOffer.technologyAvailability.expansionPeriodEnd) : undefined;

  const isEarliestActivationDateInFuture: boolean | undefined = isUggFtthTariff && tariffOffer.earliestActivationDate ?
    isExpansionPeriodEndInFuture(tariffOffer.earliestActivationDate) : undefined;

  const expansionPeriodEndLabel = (
    interpolate(cms[CmsPageContentIdentifier.FtthBeforePreparedEarliestAvailabilityDate], { date: expansionStatusEndDate })
  );

  const earliestActivationDateLabel = (
    interpolate(cms[CmsPageContentIdentifier.FtthEarliestAvailabilityDate], { date: earliestActivationDate })
  );

  const tariffBandwidthCase = (selectedTariffBandwidth: number, originalTariffBandwidth: number) => {
    if (selectedTariffBandwidth > originalTariffBandwidth) {
      return ' upsell';
    } else if (selectedTariffBandwidth < originalTariffBandwidth) {
      return ' downsell';
    } else {
      return ' same tariff';
    }
  };

  const useCaseValuewithObject = (selectedTariff: TariffOfferType, originalTariff: TariffOfferType) => {
    const tariffType = selectedTariff.accessTechnology != originalTariff.accessTechnology ? 'other tech,' : 'same tech,';
    const selectedTariffBandwidth = Number(selectedTariff.cmsTariffDescription.downloadBandwidthText.content.replace(/\D/g, ''));
    const originalTariffBandwidth = Number(originalTariff.cmsTariffDescription.downloadBandwidthText.content.replace(/\D/g, ''));
    return tariffType.concat(tariffBandwidthCase(selectedTariffBandwidth, originalTariffBandwidth));
  };

  const getAccessTechnologyFromName = (tariffName: string) => {
    if (tariffName.includes('dsl')) {
      return FixedLineAccessTechnologyType.DSL;
    }
    if (tariffName.includes('kabel')) {
      return FixedLineAccessTechnologyType.COAX;
    }
    if (tariffName.includes('ftth')) {
      return FixedLineAccessTechnologyType.FTTH;
    }
    if (tariffName.includes('fms')) {
      return FixedLineAccessTechnologyType.FMS;
    }
  };

  const useCaseValuewithName = (selectedTariff: TariffOfferType, originalTariffName: string) => {
    const tariffList = ['xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl'];
    const accessTechnologyFromOfferName = getAccessTechnologyFromName(originalTariffName);
    const tariffType = selectedTariff.accessTechnology != accessTechnologyFromOfferName ? 'other tech,' : 'same tech,';
    const selectedTariffBandwidth = tariffList.findIndex(value => selectedTariff.offerName.includes('-' + value + '-'));
    const originalTariffBandwidth = tariffList.findIndex(value => originalTariffName.includes('-' + value + '-'));
    return tariffType.concat(tariffBandwidthCase(selectedTariffBandwidth, originalTariffBandwidth));
  };

  const useCaseValue = (selectedTariff: TariffOfferType, originalTariffName: string, originalTariff?: TariffOfferType) => {
    if (originalTariff) {
      return useCaseValuewithObject(selectedTariff, originalTariff);
    } else {
      return useCaseValuewithName(selectedTariff, originalTariffName);
    }
  };

  const maxBandWidthLabel = (index: number, feature: CmsTariffContentType) => (
    isDtagEnhancementToggleEnabled && (isDtagFtthTariff || isUggFtthTariff) && index == 0 ?
      interpolate(cms[CmsPageContentIdentifier.FtthMaxBandwidthEnhancementText],
        {
          downloadSpeed: cmsTariffDescription.downloadBandwidthText.rendered,
          uploadSpeed: tariffOffer.tariffUploadBandWidth,
        })
      :
      feature.rendered
  );

  const handleClickAlternateOffer = () => {
    pushToTefDataLayer(
      window,
      buildContinueWithSelectedTariffDataPopup(
        tariffOffer.offerName,
        originalTariffOfferName,
        useCaseValue(tariffOffer, originalTariffOfferName, originalOffer)),
    );
    // this setting should happen only once, considering re-rendering
    if ((isDtagFtthTariff && !dtagSpecificAvChecked) || !offerChanged) {
      setSelectedOfferName(tariffOffer.offerName);
    }
    if (!isDtagFtthTariff || (isDtagFtthTariff && dtagSpecificAvChecked)) {
      if (!offerChanged) {
        setDtagSpecificAvChecked(false);
      }
    } else {
      setShowDTAGSpecificationView(true);
    }

  };

  return (
        <div className="offer-row">
            {offerHint && <p className="offer-hint" dangerouslySetInnerHTML={{ __html: offerHint }}/>}
            <div className="availability-offers">
                <div className="availability-details">
                    <h2 className="h3-bold">
                        <div dangerouslySetInnerHTML={{ __html: cmsTariffDescription.name.rendered }}/>
                    </h2>
                    <div className="text">
                        <ul>
                            {cmsTariffDescription.featuresShort.map((feature, index) => (
                                <li key={index}>
                                    <div dangerouslySetInnerHTML={{ __html: maxBandWidthLabel(index, feature) }}/>
                                </li>
                            ))}
                            {isDtagToggleEnabled && isBeforePrepared && isExpansionEndDateInFuture &&
                                <li>
                                    <div dangerouslySetInnerHTML={{ __html: expansionPeriodEndLabel }}/>
                                </li>
                            }
                            {isDtagToggleEnabled && isBeforePrepared && isEarliestActivationDateInFuture &&
                                <li>
                                    <div dangerouslySetInnerHTML={{ __html: earliestActivationDateLabel }}/>
                                </li>
                            }
                        </ul>
                    </div>
                </div>
                <div className="availability-prices">
                    <OfferPrice charges={charges}
                                technologyPricingLabels={technologyPricingLabels}
                                cms={cms}
                                isDtagFtthTariff={isDtagFtthTariff}
                                setShowPdpActivationCostInfo={setShowPdpActivationCostInfo}
                                setShowPdpDevelopmentCostInfo={setShowPdpDevelopmentCostInfo}
                    />
                    {(dispatchEvent) ? (
                            <a
                                className="btn btn-alpha"
                                onClick={() => {
                                  pushToTefDataLayer(
                                    window,
                                    buildContinueWithSelectedTariffDataPopup(
                                      tariffOffer.offerName,
                                      originalTariffOfferName,
                                      useCaseValue(tariffOffer, originalTariffOfferName, originalOffer)),
                                  );
                                  if (!isDtagFtthTariff || (isDtagFtthTariff && dtagSpecificAvChecked)) {
                                    setDtagSpecificAvChecked(false);
                                    dispatchEvent(new CustomEvent('tariffselected'));
                                  } else {
                                    setShowDTAGSpecificationView(true);
                                  }
                                }}
                                dangerouslySetInnerHTML={{ __html: buttonText }}
                            />
                    )
                      : (
                            <a
                                className="btn btn-invert-alpha"
                                href={(!isDtagFtthTariff || (isDtagFtthTariff && dtagSpecificAvChecked))
                                  ? url
                                  : undefined}
                                onClick={handleClickAlternateOffer}
                                dangerouslySetInnerHTML={{ __html: buttonText }}
                            />
                      )
                    }
                </div>
            </div>
        </div>
  );
};

export default Item;
